/* eslint-disable */
import React, { useState, useEffect, useContext, useRef } from 'react';
import toast from 'Utils/toast';
import formatErrorMessage from 'config/errorMessages';
import {
  Box, Typography, Button, Table, TableHead, TableBody,
  TableRow, TableCell, IconButton, Skeleton, Tooltip,
} from '@mui/material';
import { useLocation, useHistory } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import iconEye from 'Images/Icon_eye.svg';
import deleteIcon from 'Images/delete_icon.svg';
import useNetworkLoader from 'hooks/useNetworkLoader';
import ConfigContext from 'Shared/ConfigContext';
import { getCarts, deleteCart as deleteCartApi } from '../../../Services';
import validateCart from '../services';
import { validatedCart, removePanier, initPaniers } from 'Shared/ConfigContext/Actions';
import AddCartDialog from './AddCartDialog';
import useAddCart from 'hooks/useAddCart';
import ValidateCartModal from './ValidateCartModal';
import DeleteCartModal from './DeleteCartModal';

const FONT = "'Open Sans', sans-serif";

/* ── Sub-components ───────────────────────────────────────────── */
const STATUS_CONFIG = {
  'Validé':   { color: '#0AD69F', bg: '#0AD69F1D', border: '#0AD69F' },
  'En Cours': { color: '#F79F1F', bg: '#FFF3E0', border: '#F79F1F' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { color: '#8398AE', bg: '#F1F5F9', border: '#8398AE' };
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      border: `1px solid ${cfg.border}`,
      borderRadius: '20px', px: '10px', py: '3px',
      backgroundColor: cfg.bg,
    }}>
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: cfg.color }} />
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: cfg.color, fontFamily: FONT }}>
        {status}
      </Typography>
    </Box>
  );
};

const TH = ({ children }) => (
  <TableCell sx={{
    fontFamily: FONT, fontSize: '14px', fontWeight: 600, color: '#385576',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    borderBottom: '1px solid #DEE5EE', py: '8px', px: '20px',
    backgroundColor: '#fff',
  }}>
    {children}
  </TableCell>
);

const TD = ({ children, sx = {} }) => (
  <TableCell sx={{ fontFamily: FONT, fontWeight:600,fontSize: '16px', color: '#000000', borderBottom: '1px solid #F1F5F9', py: '14px', px: '20px', ...sx }}>
    {children}
  </TableCell>
);

/* ── Main component ───────────────────────────────────────────── */
const PAGE_SIZE = 10;

const CartPageV2 = () => {
  const location = useLocation();
  const history = useHistory();
  const { callApi } = useNetworkLoader();
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const mounted = useRef(false);
  const { dispatch, state: { expeditions, paniers: contextPaniers } } = useContext(ConfigContext);
  const [deleteCartId, setDeleteCartId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [validateCartObj, setValidateCartObj] = useState(null);
  const [validateLoading, setValidateLoading] = useState(false);
  const [validateError, setValidateError] = useState(null);

  const loadCarts = () => {
    setLoading(true);
    setPage(0);
    callApi(() => getCarts(), (data) => { setCarts(data || []); dispatch(initPaniers(data || [])); setLoading(false); }, false);
  };

  const { showAddDialog, setShowAddDialog, adding, handleAddCart } = useAddCart({ onSuccess: loadCarts });

  useEffect(() => {
    mounted.current = true;
    loadCarts();
  }, []);

  useEffect(() => {
    if (mounted.current) {
      setCarts(contextPaniers || []);
      setPage(0);
    }
  }, [contextPaniers?.length]);

  useEffect(() => {
    if (location.state?.openAddDialog) {
      setShowAddDialog(true);
    }
  }, [location.state?.openAddDialog]);

  const totalAmount = carts.reduce(
    (sum, cart) => sum + (cart.totalNetTtc != null ? Number(cart.totalNetTtc) : (cart.products || []).reduce((s, p) => {
      const remise = (p.remise || 0) + (p.remiseExceptionnelle || 0);
      return s + (p.price || 0) * (p.quantity || 0) * (1 - remise / 100);
    }, 0)),
    0,
  );

const handleDeleteCart = (id) => {
    const cartName = carts.find((c) => c.id === id)?.name;
    setDeleting(true);
    callApi(
      () => deleteCartApi(id),
      () => {
        dispatch(removePanier(cartName));
        loadCarts();
        setDeleteCartId(null);
        setDeleting(false);
      },
      true,
      () => { setDeleting(false); },
    );
  };

  const openValidateDialog = (id) => {
    setValidateCartObj(carts.find((c) => c.id === id));
  };

  const handleValidateConfirm = (expedition, comment) => {
    if (!validateCartObj) return;
    const body = {
      expeditionId: expedition.id,
      commercialId: null,
      comment,
      name: validateCartObj.name,
      products: (validateCartObj.products || []).map((p) => ({
        quantity: p.quantity,
        reference: p.reference,
        isExist: true,
      })),
    };
    setValidateError(null);
    setValidateLoading(true);
    callApi(
      () => validateCart(validateCartObj.id, body),
      () => {
        dispatch(validatedCart(validateCartObj.name));
        setValidateCartObj(null);
        setValidateLoading(false);
        setValidateError(null);
        loadCarts();
      },
      true,
      (e) => {
        setValidateLoading(false);
        const msg = e?.response?.data?.statusLabel;
        if (msg) setValidateError(formatErrorMessage(msg));
      },
    );
  };

  const paginatedCarts = carts.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(carts.length / PAGE_SIZE);

  /* ── Cart list view ──────────────────────────────────────────── */
  return (
    <Box sx={{ backgroundColor: '#F8FAFC', pt: '90px', px: { xs: '16px', md: '40px' }, pb: '40px', mt: '-80px', fontFamily: FONT, '& *': { fontFamily: FONT } }}>

      {/* Header */}
      <Box sx={{ mb: '24px' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: '16px',mt:'8px'}}>
          <Box>
            <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#000000' }}>
              Mes paniers
            </Typography>
            {loading
              ? <Skeleton variant="text" width={140} height={20} sx={{ mt: '2px' }} />
              : <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#385576' }}>
                  Vous avez {carts.length} panier{carts.length > 1 ? 's' : ''}
                </Typography>
            }
          </Box>
          <Button
            onClick={() => setShowAddDialog(true)}
            startIcon={<AddIcon sx={{ fontSize: 16,mr:'-6px' }} />}
            sx={{
              backgroundColor: '#007BC6', color: '#fff', fontSize: '14px', fontWeight: 600,
              textTransform: 'none', borderRadius: '8px', px: '20px', height: '46px',width:'200px',
              boxShadow: '0px 5px 10px #626B7D12', '&:hover': { backgroundColor: '#0069A8' },
            }}
          >
             Ajouter un panier
          </Button>
        </Box>
      </Box>

      {/* Content: table + right card */}
      <Box sx={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>

        {/* Table */}
        <Box sx={{ flex: 1, minWidth: 0, backgroundColor: '#fff', borderRadius: '8px 8px 0 0', border: '1px solid #DEE5EE', overflow: 'hidden', mt: '-6px', mb: '18px' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TH>Panier</TH>
                <TH>Date</TH>
                <TH>Statut</TH>
                <TH>Nombre de produits</TH>
                <TH>Total Net TTC</TH>
                <TH>Actions</TH>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TD><Skeleton variant="text" width={120} height={22} /></TD>
                    <TD><Skeleton variant="text" width={140} height={22} /></TD>
                    <TD><Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: '20px' }} /></TD>
                    <TD><Skeleton variant="text" width={30} height={22} /></TD>
                    <TD><Skeleton variant="text" width={80} height={22} /></TD>
                    <TD>
                      <Box sx={{ display: 'flex', gap: '4px' }}>
                        <Skeleton variant="circular" width={28} height={28} />
                        <Skeleton variant="circular" width={28} height={28} />
                        <Skeleton variant="circular" width={28} height={28} />
                      </Box>
                    </TD>
                  </TableRow>
                ))
              ) : carts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: '60px', color: '#8F8F8F', fontSize: '14px' }}>
                    Aucun panier trouvé
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCarts.map((cart) => {
                  const cartTotal = cart.totalNetTtc != null ? Number(cart.totalNetTtc) : (cart.products || []).reduce((s, p) => {
                    const remise = (p.remise || 0) + (p.remiseExceptionnelle || 0);
                    return s + (p.price || 0) * (p.quantity || 0) * (1 - remise / 100);
                  }, 0);
                  const isValidated = cart.validated === 1;
                  const statusLabel = cart.statut || (isValidated ? 'Validé' : 'En Cours');
                  return (
                    <TableRow
                      key={cart.id}
                      sx={{ '&:hover': { backgroundColor: '#FAFBFC' } }}
                    >
                      <TD sx={{ position: 'relative' }}>
                        {isValidated && (
                          <Box sx={{ position: 'absolute', left: 0, top: '6px', bottom: '6px', width: '3px', backgroundColor: '#0AD69F', borderRadius: '0 2px 2px 0' }} />
                        )}
                        <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#000000', fontFamily: FONT }}>
                          {cart.name}
                        </Typography>
                      </TD>
                      <TD>{cart.createdAt ? new Date(cart.createdAt).toLocaleString('fr-FR') : '—'}</TD>
                      <TD><StatusBadge status={statusLabel} /></TD>
                      <TD>{cart.nombreProduits ?? (cart.products || []).length}</TD>
                      <TD>
                        <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#007BC6', fontFamily: FONT }}>
                          {cartTotal.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                        </Typography>
                      </TD>
                      <TD>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Tooltip title="Voir le détail" placement="top" arrow>
                            <IconButton onClick={() => history.push(`/cart/${cart.id}`, { cart })} sx={{ p: '6px' }}>
                              <img src={iconEye} alt="view" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                            </IconButton>
                          </Tooltip>
                          {!isValidated && (cart.nombreProduits ?? (cart.products || []).length) > 0 && (
                            <Tooltip title="Valider le panier" placement="top" arrow>
                              <IconButton onClick={() => openValidateDialog(cart.id)} sx={{ color: '#0AD69F', p: '6px', '&:hover': { color: '#09c090' } }}>
                                <CheckIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {!isValidated && (
                            <Tooltip title="Supprimer le panier" placement="top" arrow>
                              <IconButton onClick={() => setDeleteCartId(cart.id)} sx={{ p: '6px' }}>
                                <img src={deleteIcon} alt="delete" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TD>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', px: '16px', py: '10px', borderTop: '1px solid #F1F5F9' }}>
              <Typography sx={{ fontSize: '13px', color: '#8397AE', fontFamily: FONT }}>
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, carts.length)} sur {carts.length}
              </Typography>
              <IconButton
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                size="small"
                sx={{ color: page === 0 ? '#DEE5EE' : '#385576' }}
              >
                <NavigateBeforeIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                size="small"
                sx={{ color: page >= totalPages - 1 ? '#DEE5EE' : '#385576' }}
              >
                <NavigateNextIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Right total card */}
        <Box sx={{
          width: '320px', flexShrink: 0,
          backgroundColor: '#fff', borderRadius: '10px',
          border: '1px solid #DEE5EE', py: '12px',px:'18px',
          position: 'sticky', top: '100px',
        }}>
          {loading ? (
            <>
              <Skeleton variant="text" width={180} height={20} sx={{ mb: '4px' }} />
              <Skeleton variant="text" width={140} height={32} />
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: '16px', fontWeight:600,color: '#8397AE', fontFamily: FONT, mb: '-2px' }}>
                Montant Total TTC ({carts.length} panier{carts.length > 1 ? 's' : ''})
              </Typography>
              <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#007BC6', fontFamily: FONT }}>
                {totalAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} Dh
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {/* Add dialog */}
      <AddCartDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onAdd={handleAddCart}
        loading={adding}
      />

      {/* Delete confirm */}
      <DeleteCartModal
        open={Boolean(deleteCartId)}
        onClose={() => setDeleteCartId(null)}
        onConfirm={() => handleDeleteCart(deleteCartId)}
        loading={deleting}
      />

      <ValidateCartModal
        open={Boolean(validateCartObj)}
        onClose={() => { setValidateCartObj(null); setValidateError(null); }}
        onConfirm={handleValidateConfirm}
        expeditions={expeditions}
        loading={validateLoading}
        error={validateError}
      />
    </Box>
  );
};

export default CartPageV2;